import { parse, format, isValid } from 'date-fns';
import { size } from 'lodash';
import type { TableFilter } from 'features/misc/table/types';
import type { ComboBoxOption } from 'components/inputs/types';
import { useGetOrgType } from 'features/accessControl';

export type FilterRow = {
  id: string | number;
  column: string;
  text?: string;
  dd: ComboBoxOption[];
  dateFrom?: Date | null;
  dateTo?: Date | null;
  date?: Date | null;
  numberFrom?: number | '';
  numberTo?: number | '';
  checked?: boolean;
};

export const getNewFilterRow = (): FilterRow => ({
  id: crypto.randomUUID(),
  column: '',
  text: '',
  dd: [],
  dateFrom: null,
  dateTo: null,
  numberFrom: '',
  numberTo: '',
  checked: false,
});

export const parseDate = (date: string, formatStr = 'yyyy-MM-dd'): Date | null => {
  let parsed = null;
  if (date) {
    parsed = new Date(parse(date, formatStr, new Date()));
  }
  return isValid(parsed) ? parsed : null;
};

export const formatDate = (date: Date | null | undefined, formatStr = 'yyyy-MM-dd'): string => {
  return date ? format(date, formatStr) : '';
};

export const parseNumber = (num: string | number): number => {
  if (typeof num === 'string' && num) {
    const parsed = isNaN(num as unknown as number) ? num : parseInt(num);
    return parsed as unknown as number;
  }
  return (num ?? '') as number;
};

// get selected filter as a string
export const getValueForUrl = (
  filter: FilterRow | null,
  filters: TableFilter[]
): string | null | Record<string, string> => {
  if (!filter) {
    return null;
  }
  const selected = filters.find(f => f.id === filter.column);
  if (selected) {
    if (selected.type === 'text' && typeof filter.text === 'string' && filter.text) {
      return filter.text;
    }
    if (selected.type === 'dateRange' && (filter.dateFrom || filter.dateTo)) {
      return `${formatDate(filter.dateFrom)},${formatDate(filter.dateTo)}`;
    }
    if (selected.type === 'date' && filter.date) {
      return `${formatDate(filter?.date)}`;
    }
    if (selected.type === 'dd' && filter.dd?.length) {
      const ids = filter.dd?.map(txt => txt.id).join(',');
      const labels = filter.dd?.map(txt => txt.label).join(',');
      const colors = filter.dd?.map(txt => txt.customProps?.color || '').join(',');
      return {
        [selected.id]: ids,
        [`${selected.id}Labels`]: labels,
        [`${selected.id}Colors`]: colors,
      };
    }
    if (selected.type === 'number' && (filter.numberFrom || filter.numberTo)) {
      return `${filter.numberFrom}_${filter.numberTo}`;
    }
    if (selected.bottomSwitch) {
      return String(Boolean(filter?.checked));
    }
  }
  return null;
};

// format the filter for url search params
export const formatForUrl = (
  selected: FilterRow[],
  filters: TableFilter[],
  tableFilter: Record<string, string | null> = {}
) => {
  return filters?.reduce((acc: Record<string, string | null>, filter) => {
    const selectedFilter = selected.find(f => f.column === filter.id) || null;
    const values = getValueForUrl(selectedFilter, filters);
    if (values && typeof values === 'object' && !Array.isArray(values)) {
      Object.entries(values).forEach(([key, value]) => {
        if (value || key in tableFilter) {
          acc[key] = value;
        }
      });
    } else if (values || filter.id in tableFilter) {
      acc[filter.id] = values as string | null;
    }
    return acc;
  }, {});
};

// format the filter from url for form
export const formatForForm = (filterState: Record<string, string>, filters: TableFilter[]) => {
  const values: FilterRow[] = [];
  if (size(filterState) > 0) {
    filters.forEach(filter => {
      if (filterState[filter.id] !== undefined && filterState[filter.id] !== null) {
        const value = filterState[filter.id];
        const row = getNewFilterRow();
        row.column = filter.id;
        if (filter.type === 'text') {
          row.text = value;
        }
        if (filter.bottomSwitch) {
          row.checked = value as unknown as boolean;
        }
        if (filter.type === 'dateRange') {
          const [dateFrom, dateTo] = value.split(',');
          row.dateFrom = parseDate(dateFrom);
          row.dateTo = parseDate(dateTo);
        }
        if (filter.type === 'date') {
          row.date = parseDate(value);
        }
        if (filter.type === 'dd') {
          row.dd = value.split(',').map((val, index) => {
            const id = isNaN(val as unknown as number) ? val : parseInt(val);
            const label =
              String(filterState[`${filter?.id}Labels`]?.split(',')?.[index] ?? '') ?? '';
            const color = filterState[`${filter?.id}Colors`]?.split(',')?.[index] ?? '';
            return {
              id,
              label: filter?.options?.find(o => o?.id === val)?.label || label || '',
              customProps: { color: color || 'inherit' },
            };
          });
        }
        if (filter.type === 'number') {
          const [from, to] = value.split('_');
          row.numberFrom = parseNumber(from);
          row.numberTo = parseNumber(to);
        }
        values.push(row);
      }
    });
  }
  return values;
};

// filter out filters not accessable by user
export const useFilters = (filter: TableFilter[] = []) => {
  const orgType = useGetOrgType();
  return filter?.filter(f => !f.orgType || f.orgType === orgType);
};
